// @soft-depth
import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, keyframes } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { Check, MessageCircle, Pencil, RefreshCw } from 'lucide-react';
import DiscLetterBadge from '../../DiscLetterBadge/DiscLetterBadge';
import SoftDepthScope from '../../../design-system/SoftDepthScope';

/**
 * The pieces 招募 IDEA and Smart IDEA's 溝通方式 are both built from, so the two
 * read the same whether the adviser is recruiting or selling: a section card,
 * the suggested opener, the meeting flow, the talking points and the persona
 * card. Each takes its content from the host; nothing here knows which
 * conversation it is in.
 *
 * Built on Soft Depth. Each piece mounts its own business-mode scope, in 招募
 * IDEA too: IDEA looks the same whichever conversation it is in, as it did
 * before the redesign, so nothing here turns amber. Host content passed in as
 * children is plain markup and keeps its own classes.
 */

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

const caretBlink = keyframes`50% { opacity: 0; }`;
const personaSweep = keyframes`
  0% { transform: translateX(-30%); }
  60%, 100% { transform: translateX(30%); }
`;
const avatarBob = keyframes`
  0%, 100% { transform: translateY(0) rotate(-3deg); }
  50% { transform: translateY(-5px) rotate(3deg); }
`;

// Full text for screen readers while the typewriter performs its substring.
const SR_ONLY = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  m: '-1px',
  p: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap'
} as const;

/**
 * Types `text` out character by character while `active`. The full string also
 * sits in a visually-hidden span from the first render, so screen readers (and
 * tests, which cannot wait on jsdom timers) always see the whole line.
 */
export const TypewriterText = (props: { text: string; active?: boolean; className?: string; speed?: number }) => {
  const { text, active = true, speed = 28 } = props;
  const [shown, setShown] = useState(() => (active && !prefersReducedMotion() ? '' : text));
  useEffect(() => {
    if (!active || prefersReducedMotion()) {
      setShown(text);
      return;
    }
    setShown('');
    let i = 0;
    const timer = window.setInterval(() => {
      i += 1;
      setShown(text.slice(0, i));
      if (i >= text.length) window.clearInterval(timer);
    }, speed);
    return () => window.clearInterval(timer);
  }, [text, active, speed]);
  return (
    <span className={props.className}>
      <Box component="span" sx={SR_ONLY}>
        {text}
      </Box>
      <span aria-hidden="true">
        {shown}
        {shown.length < text.length && (
          <Box
            component="i"
            sx={{
              display: 'inline-block',
              width: '2px',
              height: '1em',
              ml: '1px',
              bgcolor: 'currentColor',
              verticalAlign: '-0.15em',
              animation: `${caretBlink} 0.8s steps(1) infinite`,
              [REDUCED_MOTION]: { animation: 'none' }
            }}
          />
        )}
      </span>
    </span>
  );
};

/**
 * 編輯 / 完成 in a card or step head — the small pale accent pill it was before
 * Soft Depth. `pressed` makes it a toggle (solid accent while editing).
 */
export const IdeaEditButton = (props: {
  label: string;
  onClick: () => void;
  pressed?: boolean;
  testId?: string;
  /** Which card it edits, as `data-kind`, when one host has several. */
  kind?: string;
  disabled?: boolean;
}) => {
  const { label, onClick, pressed, testId, kind, disabled } = props;
  return (
    <SoftDepthScope mode="business">
      <Chip
        component="button"
        type="button"
        clickable
        size="small"
        color="primary"
        variant="filled"
        aria-pressed={pressed === undefined ? undefined : pressed}
        data-testid={testId}
        data-kind={kind}
        disabled={disabled}
        onClick={onClick}
        icon={pressed ? <Check size={12} aria-hidden="true" /> : <Pencil size={12} aria-hidden="true" />}
        label={label}
        sx={{
          ml: 'auto',
          flexShrink: 0,
          fontWeight: 600,
          color: 'primary.main',
          // The pen sits as far from the left edge as the word from the right.
          '& .MuiChip-icon': { ml: 1, mr: -0.5, color: 'inherit' },
          ...(pressed && {
            '&, &:hover, &.Mui-focusVisible': {
              bgcolor: 'primary.main',
              borderColor: 'primary.main',
              color: 'common.white'
            }
          })
        }}
      />
    </SoftDepthScope>
  );
};

/** A section card: icon + title, an optional hint and action in the head. */
export type IdeaCardProps = {
  id: string;
  icon: React.ReactNode;
  title: string;
  hint?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  testId?: string;
  className?: string;
  cardRef?: React.Ref<HTMLElement>;
  /** Outlines the card as the one the dialog was opened for. */
  focused?: boolean;
} & Omit<React.HTMLAttributes<HTMLElement>, 'title' | 'id' | 'color'>;

export const IdeaCard = (props: IdeaCardProps) => {
  const {
    id,
    icon,
    title,
    hint,
    action,
    children,
    testId = 'idea-section',
    className,
    cardRef,
    focused,
    ...rest
  } = props;
  return (
    <SoftDepthScope mode="business">
      <Paper
        component="section"
        ref={cardRef}
        className={className}
        data-testid={testId}
        data-section={id}
        data-focused={focused || undefined}
        {...rest}
        sx={{
          p: 2,
          minWidth: 0,
          // Outlined, not tinted: a tint on one card of four reads as "this one
          // is different", an outline reads as "start here".
          ...(focused && { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' })
        }}
      >
        <Typography
          variant="h6"
          component="h3"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            m: 0,
            mb: 1.5,
            pb: 1,
            borderBottom: '1px dashed',
            borderColor: 'divider',
            '& > svg': { flex: '0 0 auto', color: 'primary.main' }
          }}
        >
          {icon}
          {/* Takes the free space, so an action that pushes itself right
              (margin-left: auto) still lands after the hint. */}
          <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
            {title}
          </Box>
          {hint && (
            <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 400 }}>
              {hint}
            </Typography>
          )}
          {action}
        </Typography>
        {children}
      </Paper>
    </SoftDepthScope>
  );
};

/**
 * 建議開場 — the one line to walk in with, as a speech bubble from the adviser's
 * side. 換一句 cycles `lines` (a remount replays the typewriter). It closes the
 * DISC card: the adviser reads who they are talking to first, then what to say.
 */
export type IdeaOpenerProps = {
  title: string;
  /** 今日見 {name}，開口就問： */
  lead: string;
  lines: string[];
  /** Shown when `lines` is empty. */
  fallback: string;
  refreshLabel: string;
};

export const IdeaOpener = (props: IdeaOpenerProps) => {
  const { title, lead, lines, fallback, refreshLabel } = props;
  const [idx, setIdx] = useState(0);
  const line = lines.length ? lines[idx % lines.length] : fallback;
  return (
    <SoftDepthScope mode="business">
      <Stack direction="row" data-testid="recruit-talkcard" sx={{ alignItems: 'flex-start', gap: 1.25 }}>
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 30,
            height: 30,
            mt: '2px',
            borderRadius: '50%',
            bgcolor: 'primary.soft',
            color: 'primary.dark'
          }}
        >
          <MessageCircle size={16} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75, mb: 0.5 }}>
            <Typography variant="overline" sx={{ color: 'primary.dark' }}>
              {title}
            </Typography>
            <Chip
              component="button"
              type="button"
              clickable
              size="small"
              variant="outlined"
              data-testid="recruit-talkcard-refresh"
              onClick={() => setIdx(i => i + 1)}
              disabled={lines.length < 2}
              icon={<RefreshCw size={12} aria-hidden="true" />}
              label={refreshLabel}
              sx={{ ml: 'auto' }}
            />
          </Stack>
          <Typography variant="caption" component="p" sx={{ m: 0, mb: 0.75, color: 'text.secondary' }}>
            {lead}
          </Typography>
          {/* The bubble: its flat top-left corner points back at the speaker. */}
          <Typography
            key={line}
            component="blockquote"
            variant="body1"
            sx={{
              m: 0,
              px: 1.5,
              py: 1.25,
              borderRadius: '4px 12px 12px 12px',
              bgcolor: 'primary.soft',
              color: 'primary.dark',
              fontWeight: 600
            }}
          >
            <TypewriterText text={line} speed={22} />
          </Typography>
        </Box>
      </Stack>
    </SoftDepthScope>
  );
};

/**
 * 會談流程 — the meeting top to bottom as a timeline. Each step brings its own
 * content; 招募 fills 探索 / 化解 with 痛點 and 顧慮, Smart IDEA with 了解需要 and
 * 化解異議.
 */
export type IdeaFlowStep = {
  id: string;
  /** What sits in the dot: a number, or an icon for the closing step. */
  marker: React.ReactNode;
  title: string;
  minutes?: number;
  action?: React.ReactNode;
  note?: string;
  content?: React.ReactNode;
  /** The closing step: green dot, note set as body text. */
  close?: boolean;
};

export const IdeaMeetingFlow = (props: {
  steps: IdeaFlowStep[];
  minutesLabel: (n: number) => string;
  testId?: string;
}) => (
  <SoftDepthScope mode="business">
    <Box component="ol" data-testid={props.testId || 'recruit-flow'} sx={{ m: 0, p: 0, listStyle: 'none' }}>
      {props.steps.map(step => {
        // The opening and closing steps are one line to read, so it is body text.
        const noteIsBody = step.close || step.id === 'open';
        return (
          <Box
            component="li"
            key={step.id}
            data-step={step.id}
            data-close={step.close || undefined}
            sx={{
              position: 'relative',
              display: 'grid',
              gridTemplateColumns: '26px minmax(0, 1fr)',
              columnGap: 1.5,
              pb: 2.25,
              // The connecting line, from under this dot to the next one.
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 30,
                bottom: 4,
                left: 12,
                width: '2px',
                borderRadius: '1px',
                bgcolor: 'divider'
              },
              '&:last-child': { pb: 0, '&::before': { display: 'none' } }
            }}
          >
            <Typography
              component="span"
              variant="subtitle2"
              aria-hidden="true"
              sx={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 26,
                height: 26,
                borderRadius: '50%',
                fontWeight: 700,
                bgcolor: step.close ? 'success.soft' : 'primary.soft',
                color: step.close ? 'success.dark' : 'primary.dark'
              }}
            >
              {step.marker}
            </Typography>
            <Box sx={{ minWidth: 0, pt: '3px' }}>
              <Stack direction="row" sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
                <Typography component="span" variant="body1" sx={{ fontWeight: 700, color: 'grey.800' }}>
                  {step.title}
                </Typography>
                {step.minutes !== undefined && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ px: 0.875, borderRadius: '999px', bgcolor: 'grey.100', color: 'text.secondary' }}
                  >
                    {props.minutesLabel(step.minutes)}
                  </Typography>
                )}
                {step.action}
              </Stack>
              {step.note && (
                <Typography
                  component="p"
                  variant="body2"
                  sx={{
                    m: 0,
                    mt: 0.375,
                    mb: noteIsBody ? 0 : 1,
                    color: noteIsBody ? 'text.primary' : 'text.secondary'
                  }}
                >
                  {step.note}
                </Typography>
              )}
              {step.content}
            </Box>
          </Box>
        );
      })}
    </Box>
  </SoftDepthScope>
);

/**
 * How to deliver a line to THIS person's type — 「🦅 對 果斷決策型 (D)：直接給兩三個方案…」.
 * Sits under a generic answer (化解異議, 顧慮 → 化解); the answer stays the
 * same for everyone, only the delivery changes with the DISC letter.
 */
export const DiscLine = (props: { letter: string; prefix: string; text: string; testId?: string }) => (
  <SoftDepthScope mode="business">
    <Typography
      component="p"
      variant="body2"
      data-testid={props.testId || 'idea-disc-line'}
      data-letter={props.letter}
      sx={{
        m: 0,
        mt: 0.75,
        px: 1.25,
        py: 0.75,
        borderLeft: '3px solid',
        borderColor: 'primary.light',
        borderRadius: '0 8px 8px 0',
        bgcolor: 'grey.50',
        color: 'text.primary'
      }}
    >
      <Box component="strong" sx={{ mr: 0.25, fontWeight: 700, color: 'primary.dark' }}>
        {props.prefix}
      </Box>
      {props.text}
    </Typography>
  </SoftDepthScope>
);

/** 可以分享的亮點 — talking points the adviser brings up when the talk leads there. */
/** `lead` marks the point that fits this person's type best (see discPoints). */
export type IdeaPoint = { key: string; icon: string; title: string; desc: string; lead?: boolean };

export const IdeaPoints = (props: { points: IdeaPoint[]; hint: string; testId?: string; leadLabel?: string }) => (
  <SoftDepthScope mode="business">
    <Typography component="p" variant="body2" sx={{ m: 0, mb: 1.25, color: 'text.secondary' }}>
      {props.hint}
    </Typography>
    <Box
      data-testid={props.testId || 'recruit-talking-points'}
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 1.5,
        '@media (max-width: 580px)': { gridTemplateColumns: '1fr' }
      }}
    >
      {props.points.map(point => (
        <Box
          key={point.key}
          data-point={point.key}
          data-lead={point.lead || undefined}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            p: 1.75,
            border: '1px solid',
            borderColor: point.lead ? 'primary.border' : 'divider',
            borderRadius: 1.5,
            bgcolor: point.lead ? 'primary.soft' : 'background.paper'
          }}
        >
          <Typography
            component="span"
            variant="h4"
            aria-hidden="true"
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '10px',
              bgcolor: 'background.paper'
            }}
          >
            {point.icon}
          </Typography>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="strong"
              variant="body2"
              sx={{ display: 'block', fontWeight: 700, color: 'grey.900' }}
            >
              {point.title}
              {point.lead && props.leadLabel && (
                <Typography
                  component="span"
                  variant="overline"
                  sx={{
                    display: 'inline-block',
                    ml: 0.75,
                    px: 0.875,
                    borderRadius: '999px',
                    bgcolor: 'background.paper',
                    color: 'primary.dark',
                    verticalAlign: '1px'
                  }}
                >
                  {props.leadLabel}
                </Typography>
              )}
            </Typography>
            <Typography component="p" variant="caption" sx={{ m: 0, mt: 0.25, color: 'text.secondary' }}>
              {point.desc}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  </SoftDepthScope>
);

/**
 * The person as a card: their DISC type up top (set here, through 編輯), what
 * it means, host-specific blocks, and the birthday readings at the foot.
 * 招募 IDEA shows the candidate's 職場 type and strengths; Smart IDEA the
 * client's type and how to approach them.
 */
export type DiscPersonaCardProps = {
  testId: string;
  /** The letter on file; without one the card invites setting it. */
  letter?: string | null;
  emoji?: string;
  name?: string;
  /** The type's own hue (DISC_ARCHETYPES / COVER_ANIMALS); the accent without one. */
  color?: string;
  tagline?: string;
  traits?: string[];
  tendencies?: string[];
  tendencyTestId?: string;
  /** Host-specific blocks under the traits (招募: 個人核心競爭力標籤). */
  children?: React.ReactNode;
  readings?: React.ReactNode;
  readingsTitle: string;
  onSetType?: () => void;
  editLabel: string;
  setTypeLabel: string;
  emptyText: string;
};

export const DiscPersonaCard = (props: DiscPersonaCardProps) => {
  const { testId, letter, emoji, name, color, tagline, traits, tendencies, tendencyTestId } = props;
  const { children, readings, readingsTitle, onSetType, editLabel, setTypeLabel, emptyText } = props;

  const readingsBlock = readings && (
    <Box sx={{ position: 'relative', mt: 1.75, pt: 1.5, borderTop: '1px dashed', borderColor: 'hue.violet.border' }}>
      <Typography
        variant="caption"
        component="span"
        sx={{ display: 'block', mb: 1, fontWeight: 700, color: 'text.secondary' }}
      >
        {readingsTitle}
      </Typography>
      {readings}
    </Box>
  );

  if (!letter) {
    return (
      <SoftDepthScope mode="business">
        <Paper data-testid={testId} data-empty="true" sx={{ p: 2 }}>
          <Stack direction="row" sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1.25 }}>
            <Typography component="span" variant="h4" aria-hidden="true">
              ❔
            </Typography>
            <Typography component="p" variant="body2" sx={{ m: 0, flex: 1, minWidth: 0, color: 'text.secondary' }}>
              {emptyText}
            </Typography>
            {onSetType && (
              <Button size="small" variant="contained" data-testid={`${testId}-set-type`} onClick={onSetType}>
                {setTypeLabel}
              </Button>
            )}
          </Stack>
          {readingsBlock}
        </Paper>
      </SoftDepthScope>
    );
  }

  return (
    <SoftDepthScope mode="business">
      <Paper
        data-testid={testId}
        data-letter={letter}
        // The violet card it was before Soft Depth, in both modes.
        sx={{
          position: 'relative',
          p: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'hue.violet.border',
          background: (theme: Theme) =>
            `linear-gradient(160deg, ${theme.palette.hue.violet.soft} 0%, ${theme.palette.hue.indigo.soft} 55%, ${theme.palette.hue.pink.soft} 100%)`,
          boxShadow: (theme: Theme) => `0 6px 16px ${alpha(theme.palette.hue.violet.main, 0.1)}`
        }}
      >
        {/* A slow light sweep — it reads as a card worth looking at. */}
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            inset: '-40%',
            background: (theme: Theme) =>
              `linear-gradient(115deg, transparent 42%, ${alpha(
                theme.palette.common.white,
                0.65
              )} 50%, transparent 58%)`,
            animation: `${personaSweep} 3.2s ease-in-out infinite`,
            pointerEvents: 'none',
            [REDUCED_MOTION]: { animation: 'none' }
          }}
        />
        <Stack direction="row" sx={{ position: 'relative', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          {/* The big emoji with no frame, as before the redesign. */}
          <Typography
            component="span"
            variant="mascot"
            aria-hidden="true"
            sx={{
              flexShrink: 0,
              filter: (theme: Theme) => `drop-shadow(0 3px 6px ${alpha(theme.palette.hue.violet.main, 0.25)})`,
              animation: `${avatarBob} 2.6s ease-in-out infinite`,
              [REDUCED_MOTION]: { animation: 'none' }
            }}
          >
            {emoji}
          </Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" component="h3" sx={{ m: 0, fontWeight: 700, color: color || 'primary.dark' }}>
              {name}
              <DiscLetterBadge letter={letter} />
            </Typography>
            {tagline && (
              <Typography variant="caption" component="p" sx={{ m: 0, mt: 0.25, color: 'text.secondary' }}>
                {tagline}
              </Typography>
            )}
          </Box>
          {onSetType && (
            <Box sx={{ alignSelf: 'flex-start' }}>
              <IdeaEditButton label={editLabel} testId={`${testId}-set-type`} onClick={onSetType} />
            </Box>
          )}
        </Stack>

        {traits && traits.length > 0 && (
          <Stack component="ul" sx={{ position: 'relative', listStyle: 'none', m: 0, mb: 1.5, p: 0, gap: 0.75 }}>
            {traits.map((trait, i) => (
              <Typography
                component="li"
                variant="body2"
                key={i}
                sx={{
                  position: 'relative',
                  pl: 1.75,
                  color: 'text.primary',
                  '&::before': {
                    content: '"•"',
                    position: 'absolute',
                    left: 2,
                    fontWeight: 700,
                    color: 'hue.violet.main'
                  }
                }}
              >
                {trait}
              </Typography>
            ))}
          </Stack>
        )}

        {tendencies && tendencies.length > 0 && (
          <Stack direction="row" sx={{ position: 'relative', flexWrap: 'wrap', gap: 0.75 }}>
            {tendencies.map((tendency, i) => (
              <Chip
                key={i}
                size="small"
                variant="outlined"
                label={tendency}
                data-testid={tendencyTestId}
                sx={{
                  bgcolor: (theme: Theme) => alpha(theme.palette.common.white, 0.72),
                  borderColor: 'hue.violet.border',
                  color: 'hue.violet.dark'
                }}
              />
            ))}
          </Stack>
        )}

        {children && <Box sx={{ position: 'relative' }}>{children}</Box>}
        {readingsBlock}
      </Paper>
    </SoftDepthScope>
  );
};
